import { supabase } from "../../supabase";
import {
  loadOrganizationMemberAuthUserIds,
  mapMembershipRoleToLegacyRole,
  MEMBERSHIP_ROLE,
} from "../membershipHelpers";
import {
  mapLegacyRoleToMembershipRole,
  resolveMembershipRoleUpdate,
} from "../identityAuthority";
import { can, CAPABILITY } from "../permissions";
import { isGuestActor } from "../guestMode";
import { dualWriteUserRate } from "../userRate";

const USER_LIST_COLUMNS =
  "supabase_user_id, employee_name, role, email, phone, classification_name, hourly_rate, payroll_type, payroll_access, finance_access, trade_id, manager_supabase_user_id, notes";

function assertUsersAdminAccess(actor) {
  if (!actor || isGuestActor(actor)) {
    throw new Error("Not authorized to manage users.");
  }

  if (!can(actor, CAPABILITY.USERS_EDIT)) {
    throw new Error("Not authorized to manage users.");
  }
}

function assertManagerToolsAccess(actor) {
  if (!actor || isGuestActor(actor)) {
    throw new Error("Not authorized to manage employees.");
  }

  if (!can(actor, CAPABILITY.USERS_EDIT_REPORTS)) {
    throw new Error("Not authorized to manage employees.");
  }
}

async function loadProfileIdForAuthUser(supabaseUserId, client) {
  const { data: profile, error } = await client
    .from("profiles")
    .select("id")
    .eq("supabase_user_id", supabaseUserId)
    .maybeSingle();

  if (error) {
    throw error;
  }

  return profile?.id || null;
}

async function loadMembershipRoleForOrg(
  profileId,
  organizationId,
  client,
  { requireActive = true } = {},
) {
  if (!profileId || !organizationId) {
    return null;
  }

  let query = client
    .from("organization_members")
    .select(
      "id, role, finance_access, classification_name, trade_id, manager_member_id, active",
    )
    .eq("organization_id", organizationId)
    .eq("profile_id", profileId);

  if (requireActive) {
    query = query.eq("active", true);
  }

  const { data, error } = await query.maybeSingle();

  if (error) {
    throw error;
  }

  return data;
}

/**
 * Enrich a legacy users row with the current-org membership role (SoT).
 * `role` becomes the capability-facing legacy mapping; `membership_role` is raw.
 */
export function enrichUserRowWithMembership(userRow, membership) {
  if (!userRow) {
    return userRow;
  }

  if (!membership?.role) {
    return {
      ...userRow,
      membership_role: null,
      membership_id: null,
    };
  }

  return {
    ...userRow,
    membership_id: membership.id,
    membership_role: membership.role,
    role: mapMembershipRoleToLegacyRole(membership.role),
    finance_access:
      membership.finance_access ?? userRow.finance_access ?? false,
    classification_name:
      membership.classification_name ?? userRow.classification_name,
    trade_id: membership.trade_id ?? userRow.trade_id,
  };
}

export async function listAllUsers(actor, client = supabase) {
  assertUsersAdminAccess(actor);

  if (!actor?.organization_id) {
    return [];
  }

  const orgUserIds = await loadOrganizationMemberAuthUserIds(
    actor.organization_id,
    client,
  );

  if (!orgUserIds.length) {
    return [];
  }

  const { data, error } = await client
    .from("users")
    .select(USER_LIST_COLUMNS)
    .in("supabase_user_id", orgUserIds)
    .order("employee_name");

  if (error) {
    throw error;
  }

  const rows = data || [];
  if (!rows.length) {
    return [];
  }

  const { data: memberships, error: membershipError } = await client
    .from("organization_members")
    .select("id, role, finance_access, classification_name, trade_id, profile_id, profiles!inner(supabase_user_id)")
    .eq("organization_id", actor.organization_id)
    .eq("active", true);

  if (membershipError) {
    throw membershipError;
  }

  const byAuthId = new Map();
  for (const row of memberships || []) {
    const authId = row.profiles?.supabase_user_id;
    if (authId) {
      byAuthId.set(authId, row);
    }
  }

  return rows.map((userRow) =>
    enrichUserRowWithMembership(userRow, byAuthId.get(userRow.supabase_user_id)),
  );
}

export async function listManagedEmployees(
  actor,
  managerSupabaseUserId,
  client = supabase,
) {
  assertManagerToolsAccess(actor);

  if (!managerSupabaseUserId || !actor?.organization_id) {
    return [];
  }

  const orgUserIds = await loadOrganizationMemberAuthUserIds(
    actor.organization_id,
    client,
  );

  if (!orgUserIds.length) {
    return [];
  }

  const { data, error } = await client
    .from("users")
    .select(USER_LIST_COLUMNS)
    .eq("manager_supabase_user_id", managerSupabaseUserId)
    .in("supabase_user_id", orgUserIds)
    .order("employee_name");

  if (error) {
    throw error;
  }

  return data || [];
}

async function dualWriteMembershipContactFields(
  {
    supabaseUserId,
    organizationId,
    phone,
    notes,
  },
  client,
) {
  if (!organizationId) {
    return { membershipUpdated: false };
  }

  const profileId = await loadProfileIdForAuthUser(supabaseUserId, client);
  if (!profileId) {
    return { membershipUpdated: false };
  }

  const patch = {};
  if (phone !== undefined) {
    patch.phone = phone;
  }
  if (notes !== undefined) {
    patch.notes = notes;
  }

  if (!Object.keys(patch).length) {
    return { membershipUpdated: false };
  }

  const { error } = await client
    .from("organization_members")
    .update(patch)
    .eq("organization_id", organizationId)
    .eq("profile_id", profileId)
    .eq("active", true);

  if (error) {
    throw error;
  }

  return { membershipUpdated: true };
}

export async function saveManagedEmployee(
  actor,
  {
    supabase_user_id,
    phone,
    hourly_rate,
    notes,
  },
  client = supabase,
) {
  assertManagerToolsAccess(actor);

  if (!actor?.organization_id) {
    throw new Error("Organization context required to manage employees.");
  }

  const { error: profileFieldsError } = await client
    .from("users")
    .update({
      phone,
      notes,
    })
    .eq("supabase_user_id", supabase_user_id);

  if (profileFieldsError) {
    throw profileFieldsError;
  }

  await dualWriteMembershipContactFields(
    {
      supabaseUserId: supabase_user_id,
      organizationId: actor.organization_id,
      phone,
      notes,
    },
    client,
  );

  // Dual-write: membership.hourly_rate (SoT) + users.hourly_rate (compat).
  await dualWriteUserRate(
    {
      supabaseUserId: supabase_user_id,
      hourlyRate: hourly_rate,
      organizationId: actor.organization_id,
    },
    client,
  );
}

/**
 * Administration user save — membership role is authoritative for the actor's org.
 * Mirrors mapped legacy role onto users.role for Phase 1 RLS compatibility.
 * Never silently demotes organization Owner.
 */
export async function saveAdminUser(
  actor,
  {
    supabase_user_id,
    role,
    manager_supabase_user_id,
    classification_name,
    trade_id,
    finance_access,
  },
  client = supabase,
) {
  assertUsersAdminAccess(actor);

  if (!actor?.organization_id) {
    throw new Error("Organization context required to manage users.");
  }

  const profileId = await loadProfileIdForAuthUser(supabase_user_id, client);
  const membership = await loadMembershipRoleForOrg(
    profileId,
    actor.organization_id,
    client,
  );

  if (!membership?.id) {
    throw new Error(
      "Target user has no active membership in the current organization.",
    );
  }

  const nextMembershipRole = resolveMembershipRoleUpdate({
    currentMembershipRole: membership.role,
    requestedLegacyRole: role,
  });

  if (
    membership.role === MEMBERSHIP_ROLE.OWNER &&
    role &&
    mapLegacyRoleToMembershipRole(role) !== MEMBERSHIP_ROLE.ADMIN &&
    nextMembershipRole === MEMBERSHIP_ROLE.OWNER
  ) {
    // Explicit protection: Owner cannot be demoted via ordinary Admin role dropdown.
    // Keep owner; still allow non-role field updates.
  }

  const legacyMirrorRole = mapMembershipRoleToLegacyRole(nextMembershipRole);
  const nextManagerAuthId =
    manager_supabase_user_id === "" ? null : manager_supabase_user_id || null;

  let nextManagerMemberId = null;
  if (nextManagerAuthId) {
    const managerProfileId = await loadProfileIdForAuthUser(
      nextManagerAuthId,
      client,
    );
    const managerMembership = await loadMembershipRoleForOrg(
      managerProfileId,
      actor.organization_id,
      client,
    );
    if (!managerMembership?.id) {
      throw new Error(
        "Selected manager has no active membership in the current organization.",
      );
    }
    nextManagerMemberId = managerMembership.id;
  }

  const membershipPatch = {
    role: nextMembershipRole,
    classification_name: classification_name || null,
    trade_id: trade_id || null,
    manager_member_id: nextManagerMemberId,
  };

  if (typeof finance_access === "boolean") {
    membershipPatch.finance_access = finance_access;
  }

  const { error: membershipError } = await client
    .from("organization_members")
    .update(membershipPatch)
    .eq("id", membership.id)
    .eq("organization_id", actor.organization_id);

  if (membershipError) {
    throw membershipError;
  }

  const usersPatch = {
    role: legacyMirrorRole,
    manager_supabase_user_id: nextManagerAuthId,
    classification_name: classification_name || null,
    trade_id: trade_id || null,
  };

  if (typeof finance_access === "boolean") {
    usersPatch.finance_access = finance_access;
  }

  const { error } = await client
    .from("users")
    .update(usersPatch)
    .eq("supabase_user_id", supabase_user_id);

  if (error) {
    throw error;
  }

  return {
    supabase_user_id,
    membership_id: membership.id,
    membership_role: nextMembershipRole,
    role: legacyMirrorRole,
    ownerProtected:
      membership.role === MEMBERSHIP_ROLE.OWNER &&
      nextMembershipRole === MEMBERSHIP_ROLE.OWNER &&
      role !== "admin" &&
      role != null,
  };
}

export async function saveEmployeePhone(
  actor,
  { supabase_user_id, phone },
  client = supabase,
) {
  assertManagerToolsAccess(actor);

  if (!actor?.organization_id) {
    throw new Error("Organization context required to manage employees.");
  }

  const { error } = await client
    .from("users")
    .update({ phone })
    .eq("supabase_user_id", supabase_user_id);

  if (error) {
    throw error;
  }

  await dualWriteMembershipContactFields(
    {
      supabaseUserId: supabase_user_id,
      organizationId: actor.organization_id,
      phone,
    },
    client,
  );
}

/**
 * Soft-deactivate a membership in the current organization.
 * Does not delete identity, history, or ownership rows.
 * Refuses to deactivate the organization Owner.
 */
export async function setOrganizationMemberActive(
  actor,
  { supabase_user_id, active },
  client = supabase,
) {
  assertUsersAdminAccess(actor);

  if (!actor?.organization_id) {
    throw new Error("Organization context required to manage members.");
  }

  if (typeof active !== "boolean") {
    throw new Error("active must be a boolean.");
  }

  const profileId = await loadProfileIdForAuthUser(supabase_user_id, client);
  const membership = await loadMembershipRoleForOrg(
    profileId,
    actor.organization_id,
    client,
    { requireActive: false },
  );

  if (!membership?.id) {
    throw new Error(
      "Target user has no membership in the current organization.",
    );
  }

  if (membership.role === MEMBERSHIP_ROLE.OWNER && active === false) {
    throw new Error("Cannot deactivate the Organization Owner membership.");
  }

  const { error } = await client
    .from("organization_members")
    .update({ active })
    .eq("id", membership.id)
    .eq("organization_id", actor.organization_id);

  if (error) {
    throw error;
  }

  // Compatibility: when deactivating the only remaining active membership path
  // for admin tools, leave users.active untouched unless explicitly managed later.
  return {
    membership_id: membership.id,
    active,
  };
}

/** Whether a listed user may be offered as a manager assignee in the current org. */
export function isAssignableManagerUser(userRow) {
  const membershipRole = userRow?.membership_role;
  if (membershipRole) {
    return (
      membershipRole === MEMBERSHIP_ROLE.OWNER ||
      membershipRole === MEMBERSHIP_ROLE.ADMIN ||
      membershipRole === MEMBERSHIP_ROLE.MANAGER
    );
  }
  return userRow?.role === "manager" || userRow?.role === "admin";
}
